import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, Activity, Wind, Sparkles, Target, Moon } from "lucide-react";

const CATEGORY_ICON = {
  "Stress reset": Activity,
  "Focus and clarity": Target,
  "Pre-performance": Sparkles,
  "Recovery": Wind,
  "Sleep and downregulation": Moon,
  "Anxiety support": Activity,
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [practices, setPractices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [nextBooking, setNextBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, sRes, stRes, bRes] = await Promise.all([
          api.get("/practices"),
          api.get("/sessions/me"),
          api.get("/sessions/me/stats"),
          api.get("/bookings/me"),
        ]);
        setPractices(pRes.data);
        setSessions(sRes.data);
        setStats(stRes.data);
        const upcoming = bRes.data
          .filter((b) => ["pending", "confirmed"].includes(b.status))
          .sort((a, b) => (a.preferred_date + a.preferred_time).localeCompare(b.preferred_date + b.preferred_time))[0];
        setNextBooking(upcoming || null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const recommended = practices[0];
  const QuickIcon = recommended ? (CATEGORY_ICON[recommended.category] || Wind) : Wind;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <p className="overline text-primary mb-2">Dashboard</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="dashboard-greeting">
          Welcome back, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mt-2">Pick a practice, log your state, and own your day.</p>
      </motion.div>

      {/* Top grid */}
      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        {/* Quick start */}
        <Card className="lg:col-span-2 p-6 sm:p-8 bg-primary text-primary-foreground border-0">
          <div className="flex items-start justify-between gap-4 sm:gap-6">
            <div className="min-w-0">
              <p className="overline opacity-80 mb-2">Quick start</p>
              <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                {recommended ? recommended.title : "Loading..."}
              </h2>
              <p className="mt-3 opacity-90 max-w-md text-sm leading-relaxed">
                {recommended ? recommended.purpose : "Personalized practice loading."}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  className="rounded-full bg-white text-primary hover:bg-white/90"
                  onClick={() => recommended && navigate(`/practice/${recommended.id}`)}
                  data-testid="quick-start-btn"
                >
                  Start now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <span className="text-sm opacity-80">{recommended?.duration_minutes} min</span>
              </div>
            </div>
            <div className="hidden sm:flex h-20 w-20 rounded-full bg-white/15 items-center justify-center shrink-0">
              <QuickIcon className="h-9 w-9" />
            </div>
          </div>
        </Card>

        {/* Next session */}
        <Card className="p-7">
          <p className="overline text-primary mb-2">Next session</p>
          {nextBooking ? (
            <>
              <h3 className="font-display text-xl font-semibold tracking-tight" data-testid="dashboard-next-session-type">{nextBooking.session_type}</h3>
              <p className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                <CalendarClock className="h-4 w-4" /> {nextBooking.preferred_date} · {nextBooking.preferred_time}
              </p>
              <Badge variant="secondary" className="mt-4 capitalize">{nextBooking.status}</Badge>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">No upcoming session.</p>
              <Link to="/book"><Button variant="outline" size="sm" className="mt-4 rounded-full" data-testid="dashboard-book-btn">Book a session</Button></Link>
            </>
          )}
        </Card>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        <StatCard label="Practices completed" value={stats?.total_sessions ?? 0} testid="stat-total" />
        <StatCard label="Avg stress reduction" value={stats?.avg_stress_reduction ?? 0} suffix=" pts" testid="stat-stress" />
        <StatCard label="Avg focus gain" value={stats?.avg_focus_gain ?? 0} suffix=" pts" testid="stat-focus" />
        <StatCard label="Last sleep quality" value={stats?.last_sleep_quality ?? "—"} suffix={stats?.last_sleep_quality != null ? "/10" : ""} testid="stat-sleep" />
      </div>

      {/* Recommended library */}
      <div className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="overline text-primary mb-2">Library</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight">Recommended for you</h2>
          </div>
          <Link to="/library" className="text-sm text-primary font-medium hover:underline" data-testid="dashboard-view-library">View all →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {practices.slice(0, 3).map((p) => (
            <PracticeCard key={p.id} practice={p} onClick={() => navigate(`/practice/${p.id}`)} />
          ))}
        </div>
      </div>

      {/* Recent sessions */}
      <div className="mt-14">
        <p className="overline text-primary mb-2">Recent activity</p>
        <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Your recent sessions</h2>
        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground text-sm">No sessions logged yet. Try your first practice above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="recent-sessions-list">
              {sessions.slice(0, 6).map((s) => (
                <li key={s.id} className="px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.practice_title}</p>
                    <p className="text-xs text-muted-foreground">{new Date(s.completed_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground shrink-0">
                    <span><span className="text-foreground font-medium">−{s.stress_before - s.stress_after}</span> stress</span>
                    <span><span className="text-foreground font-medium">+{s.focus_after - s.focus_before}</span> focus</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix = "", testid }) {
  return (
    <Card className="p-6" data-testid={testid}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight">{value}{suffix}</p>
    </Card>
  );
}

function PracticeCard({ practice, onClick }) {
  return (
    <Card className="p-6 hover:border-primary/40 transition-colors cursor-pointer" onClick={onClick} data-testid={`practice-card-${practice.id}`}>
      <div className="flex items-center justify-between mb-3">
        <Badge variant="secondary" className="text-xs">{practice.category}</Badge>
        <span className="text-xs text-muted-foreground">{practice.duration_minutes} min</span>
      </div>
      <h3 className="font-display text-lg font-semibold tracking-tight">{practice.title}</h3>
      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{practice.purpose}</p>
      <Button variant="ghost" size="sm" className="mt-4 -ml-3 text-primary">Start <ArrowRight className="ml-1 h-3 w-3" /></Button>
    </Card>
  );
}
